<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-dropdown right
                          no-caret
                          variant="link"
                          boundary="window"
                          toggle-class="list-view-icon-btn list-view-icon-btn--primary"
                          v-if="hasUnread || hasArchivable"
                          v-b-tooltip.hover.bottom
                          title="Acciones rápidas">
                <template #button-content>
                  <AppIcon name="lightning" label="Acciones rápidas" />
                </template>
                <b-dropdown-item v-if="hasUnread" @click="markAllRead()">
                  <AppIcon name="check2-all" class="mr-1" /> Marcar todas leídas
                </b-dropdown-item>
                <b-dropdown-item v-if="hasArchivable" @click="archiveAll()">
                  <AppIcon name="archive" class="mr-1" /> Archivar todas
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 220px; max-width: 320px;">
              <label class="filter-label">Buscar</label>
              <b-form-input type="text" size="sm" placeholder="Título o mensaje" v-model="filter.search"></b-form-input>
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Desde</label>
              <b-form-input type="date" size="sm" v-model="filter.date_start"></b-form-input>
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Hasta</label>
              <b-form-input type="date" size="sm" v-model="filter.date_end"></b-form-input>
            </div>
            <div class="list-view-filter" style="flex: 1 1 160px; max-width: 200px;">
              <label class="filter-label">Estado</label>
              <b-form-select size="sm" v-model="filter.status" :options="arr.select.status"></b-form-select>
            </div>
            <b-button variant="outline-dark" size="sm" @click="search()" v-b-tooltip.hover title="Aplicar filtros"
                      style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card>
        <b-row>
          <b-col sm="12">
            <b-table class="app-table"
                  responsive
                  :stacked="isMobileViewport ? 'md' : false"
                  sticky-header="60vh"
                  :hover="true"
                  :small="true"
                  :fixed="true"
                  :items="table.items"
                  :fields="table.fields"
                  :current-page="table.currentPage"
                  :per-page="table.perPage"
                  :tbody-tr-class="rowClass"
                  @row-clicked="onRowClicked"
                  v-if="table.items.length">

              <template v-slot:table-colgroup="scope">
                <col v-for="field in scope.fields" :key="field.key" :style="{ width: field.width }">
              </template>

              <template v-slot:cell(title)="data">
                <div class="d-flex align-items-center">
                  <!-- Solo se muestra avatar si la notificación trae un ícono
                       propio del backend — el fallback de campanita genérica
                       se sacó: repetía la misma imagen en cada fila sin
                       aportar nada (la columna ya se llama "Notificación"). -->
                  <b-avatar :src="data.item.icon" size="25px" class="mr-2" v-if="data.item.icon"></b-avatar>
                  <div class="notifications-inbox-title-col">
                    <div class="text-truncate" v-b-tooltip.hover.bottom :title="data.item.title">
                      <strong :class="{ 'text-muted': data.item.archived }">{{ data.item.title }}</strong>
                    </div>
                    <div class="small text-muted text-truncate"
                         v-b-tooltip.hover.bottom
                         :title="plainNotificationMessage(data.item.message)"
                         v-if="data.item.message">
                      {{ plainNotificationMessage(data.item.message) }}
                    </div>
                  </div>
                </div>
              </template>

              <template v-slot:cell(module)="data">
                <b-badge variant="info" v-if="data.item.module">
                  {{ data.item.module.title || data.item.module.name }}
                </b-badge>
                <span class="small text-muted" v-else>Sistema</span>
              </template>

              <template v-slot:cell(created_at)="data">
                <span v-b-tooltip.hover.bottom :title="formatDate(data.item.created_at)">
                  {{ capitalize(formatRelative(data.item.created_at)) }}
                </span>
              </template>

              <template v-slot:cell(read)="data">
                <b-badge variant="secondary" v-if="data.item.archived">ARCHIVADA</b-badge>
                <b-badge variant="success" v-else-if="data.item.read">LEÍDA</b-badge>
                <b-badge variant="warning" v-else>SIN LEER</b-badge>
              </template>

              <template v-slot:cell(f_action)="data">
                <b-dropdown right no-caret size="sm" variant="link" boundary="window" toggle-class="app-table-action-toggle">
                  <template #button-content>
                    <AppIcon name="more_vert" label="Acciones" />
                  </template>

                  <b-dropdown-item v-if="data.item.url" @click="openItem(data.item)">
                    <AppIcon name="external" class="mr-1" /> Abrir enlace
                  </b-dropdown-item>
                  <b-dropdown-item v-if="!data.item.read" @click="markReadItem(data.item)">
                    <AppIcon name="check2" class="mr-1" /> Marcar como leída
                  </b-dropdown-item>
                  <b-dropdown-item v-if="!data.item.archived" @click="archiveItem(data.item)">
                    <AppIcon name="archive" class="mr-1" /> Archivar
                  </b-dropdown-item>
                  <b-dropdown-item v-if="data.item.archived" @click="restoreItem(data.item)">
                    <AppIcon name="arrow-repeat" class="mr-1" /> Restaurar
                  </b-dropdown-item>
                </b-dropdown>
              </template>

            </b-table>
            <EmptyState v-else
                        icon="notifications"
                        title="No se encontraron notificaciones"
                        description="Ajustá los filtros o el rango de fechas para ampliar la búsqueda." />

            <div class="app-table-footer">
              <span class="list-view-toolbar__count">{{ table.items.length }} notificaciones</span>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- DRAWER: detalle de la notificación seleccionada -->
      <b-sidebar
          id="sidebar-notification-detail"
          v-model="detailDrawer"
          right
          shadow
          backdrop
          title="Detalle de la notificación"
          width="420px"
          @hidden="hideSub">
        <div class="list-view-drawer" v-if="itemSelected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code">
                {{ itemSelected.module ? (itemSelected.module.title || itemSelected.module.name) : 'Sistema' }}
              </span>
              <div :class="{ 'text-muted': itemSelected.archived }">{{ itemSelected.title }}</div>
              <div class="list-view-drawer__meta" v-b-tooltip.hover :title="formatDate(itemSelected.created_at)">
                {{ capitalize(formatRelative(itemSelected.created_at)) }}
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoPrev"
                        v-b-tooltip.hover
                        title="Notificación anterior"
                        @click="goPrev()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm"
                        variant="outline-secondary"
                        :disabled="!canGoNext"
                        v-b-tooltip.hover
                        title="Notificación siguiente"
                        @click="goNext()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div>
            <div class="list-view-drawer__section-title">
              <AppIcon name="chat" /> Mensaje
            </div>
            <div class="notifications-inbox-drawer-message" v-if="itemSelected.message" v-html="renderNotificationMessage(itemSelected.message)"></div>
            <div class="notifications-inbox-drawer-message" v-else>Sin detalle adicional</div>
          </div>

          <!-- En mobile no hay kebab en la fila (ver loadFieldTable): las
               mismas acciones quedan acá, ya desplegadas. En desktop, esas
               mismas acciones TAMBIÉN se muestran acá, además del kebab de
               la fila — evita volver a la tabla con el registro ya abierto. -->
          <div class="list-view-drawer__actions">
            <b-button size="sm" variant="outline-secondary" v-if="itemSelected.url" @click="openUrl(itemSelected.url)">
              <AppIcon name="external" class="mr-1" /> Abrir enlace
            </b-button>
            <b-button size="sm" variant="outline-secondary" v-if="!itemSelected.archived" @click="archiveItem(itemSelected)">
              <AppIcon name="archive" class="mr-1" /> Archivar
            </b-button>
            <b-button size="sm" variant="outline-secondary" v-else @click="restoreItem(itemSelected)">
              <AppIcon name="arrow-repeat" class="mr-1" /> Restaurar
            </b-button>
          </div>
        </div>
      </b-sidebar>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" /> Volver
          </b-button>

          <nav>
            <b-pagination class="mb-0"
                          size="sm"
                          pills=""
                          :total-rows="table.items.length"
                          :per-page="table.perPage"
                          v-model="table.currentPage" />
          </nav>
        </div>
      </b-card>

    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import moment from 'moment'
  import { renderNotificationMessage, plainNotificationMessage } from '@/handler/notificationMessage'

  export default {
    components: {
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.NOTIFICACIONES,
          profile_id: Profiles.PERSONAL,
          view_reference: 'inbox',
          elements: {}
        },
        table: {
          items: [],
          fields: [],
          currentPage: 1,
          perPage: 20,
        },
        arr: {
          select: {
            status: [
              { value: '', text: 'TODAS' },
              { value: 'unread', text: 'SIN LEER' },
              { value: 'read', text: 'LEÍDAS' },
              { value: 'archived', text: 'ARCHIVADAS' },
            ],
          }
        },
        filter: {
          search: '',
          date_start: '',
          date_end: '',
          status: '',
        },
        // Panel de filtros: cerrado por default; el usuario que lo abre
        // queda con su preferencia persistida (ver watcher filtersOpen).
        filtersOpen: false,
        // stacked (modo card mobile) y sticky-header son mutuamente
        // excluyentes en BootstrapVue — stacked solo se activa cuando el
        // viewport realmente está en mobile (ver mounted/mobileMql).
        isMobileViewport: false,
        itemSelected: null,
        detailDrawer: false,
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_notifications_inbox') == 'true'

      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.loadFieldTable()
      this.search().then(() => this.openFromQuery())

      this._start = true
      this._active = true
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    activated() {
      this._active = true

      if (this._start) {
        this._start = false
        return
      }

      this.search().then(() => this.openFromQuery())
    },
    deactivated() {
      this._active = false
    },
    computed: {
      hasUnread() {
        if (this.filter.status == 'archived') return false
        return this.table.items.some(item => !item.read && !item.archived)
      },
      hasArchivable() {
        if (this.filter.status == 'archived') return false
        return this.table.items.some(item => !item.archived)
      },
      // NAVEGACION DEL DRAWER (anterior/siguiente sobre las filas cargadas)
      currentIndex() {
        if(!this.itemSelected) return -1
        return this.table.items.findIndex(item => item.id === this.itemSelected.id)
      },
      canGoPrev() {
        return this.currentIndex > 0
      },
      canGoNext() {
        return this.currentIndex >= 0 && this.currentIndex < this.table.items.length - 1
      },
    },
    watch: {
      filtersOpen: function (value) {
        StorageLocal.setValue('filters_open_notifications_inbox', value ? 'true' : 'false')
      },
      // Si el viewport cruza el breakpoint mobile (resize/rotación), las
      // clases de alineación y el field de acciones tienen que recalcularse.
      isMobileViewport() {
        this.loadFieldTable()
      },
      // Click en la campana estando ya en la bandeja: cambia solo el query,
      // no hay mounted/activated. Con keep-alive el watcher también corre
      // con la vista oculta; ahí lo resuelve activated() al volver.
      '$route.query.id'(id) {
        if (id && this._active) this.openFromQuery()
      },
    },
    methods: {
      renderNotificationMessage,
      plainNotificationMessage,

      // RESPONSIVE
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      // Alineación de columna: en mobile (modo stacked) el valor pasa a
      // apilarse debajo de su label, así que la alineación de desktop
      // (centrado/derecha) no tiene sentido acá.
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      loadFieldTable() {
        this.table.fields = []
        // Ancho explícito en las 5 columnas (incluida la identidad): con
        // :fixed="true" un field sin width se queda con TODO el espacio
        // sobrante en vez de una porción proporcional, y estira la columna
        // mucho más de lo que su contenido necesita.
        this.table.fields.push({key: 'title', label: 'Notificación', class: "align-middle " + this.alignClass('text-left'), width: "320px"})
        this.table.fields.push({key: 'module', label: 'Origen', class: "align-middle " + this.alignClass('text-left'), width: "140px"})
        this.table.fields.push({key: 'created_at', label: 'Fecha', class: "align-middle " + this.alignClass('text-left'), width: "130px"})
        this.table.fields.push({key: 'read', label: 'Estado', class: "align-middle " + this.alignClass('text-center'), width: "110px"})
        // En mobile no hay kebab por fila: las acciones se muestran ya
        // desplegadas en el drawer (ver .list-view-drawer__actions).
        if(!this.isMobileViewport) {
          this.table.fields.push({key: 'f_action', label: '', class: "align-middle " + this.alignClass('text-center'), width: "50px"})
        }
      },
      search() {
        let loader = this.$loading.show()

        var result = serviceAPI.listInternal(this.filter)
        return result.then((response) => {
          loader.hide()
          this.table.items = response.data
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      // ?id=X (click en la campana): abre el detalle de esa notificación.
      // Si no está en lo cargado (llegó recién) se recarga una vez antes de
      // darla por no encontrada.
      openFromQuery(reload = true) {
        var id = this.$route.query.id
        if (!id) return

        var item = this.table.items.find(row => String(row.id) === String(id))
        if (!item && reload) {
          this.search().then(() => this.openFromQuery(false))
          return
        }

        // Limpia el query apenas se consume: si no, volver a la pestaña
        // (activated, keep-alive) reabriría el detalle.
        this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})

        if (item) {
          this.openSub(item)
        } else {
          this.$awn.warning('La notificación no aparece en el listado con los filtros actuales')
        }
      },
      // Click en la fila abre el drawer de detalle, salvo que el click haya
      // sido sobre un control (el dropdown de acciones, un link, etc.).
      onRowClicked(item, index, event) {
        if(!item) return

        if(event && event.target) {
          const ignoreTags = ['INPUT', 'BUTTON', 'A', 'SELECT', 'TEXTAREA', 'LABEL', 'SVG', 'PATH']
          if(ignoreTags.includes(event.target.tagName)) return
          if(event.target.closest('.dropdown')) return
        }

        this.openSub(item)
      },
      // Resalta en la tabla la fila del registro que está abierto en el
      // drawer; si no hay drawer abierto, distingue sin leer/archivada.
      rowClass(item, type) {
        if(type !== 'row' || !item) return ''
        if(this.detailDrawer && this.itemSelected && item.id === this.itemSelected.id) {
          return 'app-table-row-active'
        }
        if(item.archived) return 'notifications-inbox-row-archived'
        if(!item.read) return 'notifications-inbox-row-unread'
        return ''
      },
      openSub(item) {
        this.itemSelected = item
        this.detailDrawer = true

        if (!item.read) {
          serviceAPI.markInternalRead(item.id).then(() => { item.read = true })
        }
      },
      hideSub() {
        this.itemSelected = null
        this.detailDrawer = false
      },
      // Anterior/siguiente desde el drawer: reusa openSub() con el vecino,
      // así el contenido y el resaltado de fila se actualizan por el mismo
      // camino que un click directo en la tabla.
      goPrev() {
        if(!this.canGoPrev) return
        var item = this.table.items[this.currentIndex - 1]
        if(item) this.openSub(item)
      },
      goNext() {
        if(!this.canGoNext) return
        var item = this.table.items[this.currentIndex + 1]
        if(item) this.openSub(item)
      },
      openItem(item) {
        if (!item.read) {
          serviceAPI.markInternalRead(item.id)
            .then(() => {
              item.read = true
              if (item.url) this.openUrl(item.url)
            })
        } else if (item.url) {
          this.openUrl(item.url)
        }
      },
      // Link interno del sistema: navega en la misma pestaña. Link externo
      // (otro dominio): se abre en una pestaña nueva para no perder el contexto actual.
      openUrl(url) {
        let isExternal = false
        try {
          isExternal = new URL(url, window.location.origin).hostname !== window.location.hostname
        } catch (e) {
          isExternal = false
        }

        if (isExternal) {
          window.open(url, '_blank')
        } else {
          window.location.href = url
        }
      },
      markReadItem(item) {
        serviceAPI.markInternalRead(item.id)
          .then(() => {
            item.read = true
          })
      },
      markAllRead() {
        serviceAPI.markAllInternalRead()
          .then(() => {
            this.table.items.forEach(item => { item.read = true })
            this.$awn.success("Notificaciones marcadas como leídas")
          })
      },
      archiveItem(item) {
        serviceAPI.archiveInternal(item.id)
          .then(() => {
            this.$awn.success("Notificación archivada")
            if(this.itemSelected && this.itemSelected.id === item.id) {
              this.hideSub()
            }
            this.search()
          })
          .catch(error => {
            this.$awn.alert(Error.showError(error))
          })
      },
      restoreItem(item) {
        serviceAPI.restoreInternal(item.id)
          .then(() => {
            this.$awn.success("Notificación restaurada")
            if(this.itemSelected && this.itemSelected.id === item.id) {
              this.hideSub()
            }
            this.search()
          })
          .catch(error => {
            this.$awn.alert(Error.showError(error))
          })
      },
      archiveAll() {
        this.confirmDialog('¿Archivar todas las notificaciones?', {
          title: 'Archivar todas',
          icon: 'archive',
          okTitle: 'ARCHIVAR',
        })
        .then(value => {
          if (value) {
            serviceAPI.archiveAllInternal()
              .then(() => {
                this.$awn.success("Notificaciones archivadas")
                if(this.itemSelected) this.hideSub()
                this.search()
              })
              .catch(error => {
                this.$awn.alert(Error.showError(error))
              })
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      // Helper reusable de confirms — ver docs/modal-interfaces/README.md.
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      formatDate(date) {
        return moment(date).format('DD/MM/YYYY HH:mm')
      },
      // Lenguaje humano ("hace 3 horas") en vez de la fecha cruda — la
      // fecha exacta sigue disponible en el tooltip (formatDate).
      formatRelative(date) {
        return moment(date).fromNow()
      },
      capitalize(text) {
        if (!text) return ''
        return text.charAt(0).toUpperCase() + text.slice(1)
      },
    }
  }
</script>
<style scoped>
  .notifications-inbox-title-col {
    min-width: 0;
    flex: 1 1 auto;
  }
  .notifications-inbox-row-unread td {
    box-shadow: inset 3px 0 0 0 var(--app-color-warning);
    background-color: var(--app-color-warning-soft);
  }
  .notifications-inbox-row-archived td {
    box-shadow: inset 3px 0 0 0 var(--app-color-border-strong);
    color: var(--app-color-text-muted);
  }
</style>
<style>
  /* El CSS de todo lo que vive dentro de un b-sidebar va sin scoped: el
     sidebar se monta fuera del componente y las reglas scoped no le aplican. */
  .notifications-inbox-drawer-message {
    white-space: pre-line;
    word-break: break-word;
    font-size: var(--app-text-sm);
    color: var(--app-color-text);
  }
</style>
